import { Avaliation } from "../../types";

/**
 * Funções utilitárias para o componente AvaliacaoForm
 */

/**
 * Compara os campos do formulário original com o resultado da IA
 * e retorna um objeto com as mudanças identificadas
 */
export function getChangedFields(
  originalForm: Avaliation | null,
  aiResult: Partial<Avaliation> | null
): Record<string, { before: string; after: string }> {
  if (!originalForm || !aiResult) return {};

  const changes: Record<string, { before: string; after: string }> = {};

  const compareField = (path: string, before: unknown, after: unknown) => {
    const beforeStr =
      before !== null && before !== undefined && before !== ""
        ? String(before)
        : "";
    const afterStr =
      after !== null && after !== undefined && after !== ""
        ? String(after)
        : "";

    // Só mostra mudança se o valor depois for não vazio e diferente
    if (afterStr.trim() !== "" && beforeStr !== afterStr) {
      changes[path] = {
        before: beforeStr || "-",
        after: afterStr,
      };
    }
  };

  // Compara campos principais
  Object.keys(aiResult).forEach((key) => {
    if (key === "dobra_cutanea" || key === "biopedancia") {
      const beforeObj =
        (originalForm[key as keyof Avaliation] as Record<
          string,
          string | number
        >) || {};
      const afterObj =
        (aiResult[key as keyof typeof aiResult] as Record<
          string,
          string | number
        >) || {};
      if (afterObj && typeof afterObj === "object") {
        Object.keys(afterObj).forEach((subKey) => {
          const afterValue = afterObj[subKey];
          const afterValueStr =
            afterValue !== null &&
            afterValue !== undefined &&
            afterValue !== ""
              ? String(afterValue)
              : "";
          if (afterValueStr.trim() !== "") {
            compareField(
              `${key}.${subKey}`,
              beforeObj[subKey],
              afterObj[subKey]
            );
          }
        });
      }
    } else {
      const afterValue = aiResult[key as keyof typeof aiResult];
      const afterValueStr =
        afterValue !== null && afterValue !== undefined && afterValue !== ""
          ? String(afterValue)
          : "";
      if (afterValueStr.trim() !== "") {
        compareField(
          key,
          originalForm[key as keyof Avaliation],
          aiResult[key as keyof typeof aiResult]
        );
      }
    }
  });

  return changes;
}

/**
 * Faz o merge dos dados da IA com o formulário original,
 * mantendo valores existentes se a IA não retornar
 */
export function mergeAIResult(
  originalForm: Avaliation,
  aiResult: Partial<Avaliation>
): Partial<Avaliation> {
  const mergedData: Partial<Avaliation> = { ...originalForm };

  // Itera sobre os campos do resultado da IA
  Object.entries(aiResult).forEach(([key, value]) => {
    if (key === "dobra_cutanea" || key === "biopedancia") {
      // Para objetos aninhados, merge campos não vazios
      if (value && typeof value === "object") {
        const currentValue = mergedData[key as keyof Avaliation] as Record<
          string,
          string | number
        >;
        const newValue = { ...currentValue };
        Object.entries(value).forEach(([subKey, subValue]) => {
          const subValueStr = subValue ? String(subValue).trim() : "";
          if (subValueStr !== "") {
            newValue[subKey] = subValue as string | number;
          }
        });
        if (key === "dobra_cutanea") {
          mergedData.dobra_cutanea = newValue as Avaliation["dobra_cutanea"];
        } else if (key === "biopedancia") {
          mergedData.biopedancia = newValue as Avaliation["biopedancia"];
        }
      }
    } else {
      // Para campos simples, apenas sobrescreve se não for vazio
      const valueStr = value ? String(value).trim() : "";
      if (valueStr !== "") {
        (mergedData as Record<string, unknown>)[key] = value;
      }
    }
  });

  return mergedData;
}

/**
 * Processa mudanças em campos aninhados do formulário
 */
export function handleNestedFieldChange(
  form: Avaliation,
  name: string,
  value: string
): Avaliation {
  if (name.indexOf(".") !== -1) {
    const [mainKey, subKey] = name.split(".") as [keyof Avaliation, string];
    const currentMain = form[mainKey] as Record<string, unknown>;

    return {
      ...form,
      [mainKey]: {
        ...currentMain,
        [subKey]: value,
      },
    } as Avaliation;
  } else {
    return {
      ...form,
      [name]: value,
    } as Avaliation;
  }
}

/**
 * Aplica campos filtrados do diff ao formulário
 */
export function applyFilteredFields(
  originalForm: Avaliation,
  filteredFields: Record<string, { before: string; after: string }>
): Partial<Avaliation> {
  const mergedData: Partial<Avaliation> = { ...originalForm };

  Object.entries(filteredFields).forEach(([fieldPath, { after }]) => {
    if (fieldPath.indexOf(".") !== -1) {
      // Campo aninhado (ex: dobra_cutanea.tricipal)
      const [mainKey, subKey] = fieldPath.split(".") as [
        keyof Avaliation,
        string
      ];
      const currentValue = mergedData[mainKey] as Record<
        string,
        string | number
      >;
      const newValue = { ...currentValue };
      newValue[subKey] = after as string | number;

      if (mainKey === "dobra_cutanea") {
        mergedData.dobra_cutanea = newValue as Avaliation["dobra_cutanea"];
      } else if (mainKey === "biopedancia") {
        mergedData.biopedancia = newValue as Avaliation["biopedancia"];
      }
    } else {
      // Campo simples
      (mergedData as Record<string, unknown>)[fieldPath] = after;
    }
  });

  return mergedData;
}
